import React, { useEffect, useState, lazy, Suspense, useRef, useMemo } from 'react'
import classnames from 'classnames'
import { i18n } from '@/locale/i18n'
import constants from '@/constants'
import shareService from '@/services/share'
import questionService from '@/api/question'
import Helmet from '@/components/helmet'
import { feedUrl, categoryListUrl, marketsUrl } from '@/urls'
import { useCurrentUser } from '@/hooks'
import {
  fetchFeed,
  selectFeedMarketType,
  selectFeedMarkets,
  selectFeedMarketsError,
  selectFeedMarketsPagination,
  selectFeedMarketsStatus,
  selectMarketRepository,
} from '@/store/markets'
import { fetchFeedBanners, selectFeedBanners, selectFeedBannersStatus } from '@/store/banners'
import { selectAbTestsStatus, selectActiveMarketType, selectActiveLanguage, selectPageLayout } from '@/store/app'
import { useDispatch, useSelector, useStore } from 'react-redux'
import { addCampain } from '@/store/account'
import { useLocation, useNavigate } from 'react-router-dom'
import { getLocalePrefix, pathnameWithoutLocalePrefix } from '@/util/localePath'
import { urlSearchParams } from '@/util/urlSearchParams'
import CategoryNavigation from '@/features/category-navigation'
import FeedLoadingSkeleton from './FeedLoadingSkeleton'
import HomeTagsRow from './HomeTagsRow'
import { getCategorySeoMeta } from '@/util/seoPageMeta'

import './ViewFeed.scss'
import MarketListTemplate from '@/features/market/market-list-template'
import ErrorHandler from '@/components/ui/error-handler'
import useIsMobileSize from '@/hooks/useIsMobileSize'
import { useActiveCurrency } from '@/hooks'
import { isSettledStatus, toFeaturedMarket } from '@/features/home-highlight/toFeaturedMarket'
import HomeHighlightSkeleton from '@/features/home-highlight/HomeHighlightSkeleton'
import MidtermBanner from '@/features/home-highlight/MidtermBanner'
import useSenateControl from '@/features/home-highlight/useSenateControl'
import CategoriesSideMenuNavigation from '@/features/categories-side-menu'
import CryptoPriceSideMenu from '@/features/crypto-price'
import {
  categoryParentId,
  cryptoPriceCategoryIds,
  isCryptoPriceCategory,
  parsePriceLeafSlug,
} from '@/features/crypto-price/priceCategories'
import Modal from '@/layouts/modal/Modal'
import {
  fetchCategories,
  fetchFeaturedCategories,
  fetchMainRootCategories,
  selectCategoriesByParentId,
  selectCategoryById,
  selectCategoryStatusById,
  selectMainRootCategoryList,
  selectRootCategoriesStatus,
  selectFeaturedCategories,
  selectFeaturedCategoriesStatus,
} from '@/store/categories'
import { fetchTagsList, selectTagsList, selectTagsListStatus, getTagName, selectTempTagBySlug } from '@/store/tags'
import { searchPageTags, selectPageTags, selectPageTagsStatus } from '@/store/search'
import { CrossIcon } from '@/assets/icons'
import EmptyState from '@/components/ui/EmptyState'
import Button from '@/components/ui/button'
import { isWorldCupCategory, worldCupCoverUrl } from '@/constants/worldCup'
import { isSportsCategoryPath } from '@/constants/soccer'
import { MIDTERMS_TAG_SLUG } from '@/constants/midterms'
import MidtermMapSkeleton from '@/features/midterm-map/MidtermMapSkeleton'
import { defaultFeedOrdering } from '@/constants/feedOrdering'
import { LIST_VIEW_KIND } from '@/constants/marketView'
import {
  sanitizeFeedQueryParams,
  feedQueryParamsChanged,
  isValidFeedOrdering,
} from '@/util/feedQueryParams'

const WorldCupLobby = lazy(() => import('@/features/world-cup'))

// Custom wrapper for lazy components to handle import failures
const LazyComponentWrapper = ({ children, fallback, errorFallback, componentName, location }) => {
  return (
    <ErrorHandler
      component={componentName || 'LazyComponent'}
      location={location}
      fallback={({ error, resetErrorBoundary }) =>
        errorFallback ? (
          errorFallback
        ) : (
          <div className="flex items-center justify-center h-32 bg-surface-2 rounded-lg border border-dashed border-Neutral-3">
            <div className="text-center p-4">
              <div className="text-Neutral-4 text-sm mb-2">{i18n('common.failed_to_load', { name: componentName })}</div>
              <button
                onClick={resetErrorBoundary}
                className="text-xs px-3 py-1 bg-Neutral-3 text-Neutral-6 rounded hover:bg-Neutral-4 transition-colors">
                {i18n('common.retry')}
              </button>
            </div>
          </div>
        )
      }>
      <Suspense fallback={fallback}>{children}</Suspense>
    </ErrorHandler>
  )
}

// Lazy load components with retry logic
const HotTopic = lazy(() =>
  import('@/features/market/hot-topic').catch(err => {
    console.warn('Failed to load HotTopic component:', err)
    // Return a fallback component that shows an error
    return {
      default: () => (
        <div className="flex items-center justify-center h-32 bg-surface-2 rounded-lg border border-dashed border-Neutral-3">
          <div className="text-center p-4">
            <div className="text-Neutral-4 text-sm mb-2">Hot Topic temporarily unavailable</div>
            <div className="text-xs text-Neutral-3">{i18n('common.component_failed_to_load')}</div>
          </div>
        </div>
      ),
    }
  })
)

/** The highlight only renders from this width up; it has no mobile layout. */
const HOME_HIGHLIGHT_MIN_WIDTH = 1024

/** The strip shows the leading markets of whatever the feed is ordered by. */
const HOME_HIGHLIGHT_COUNT = 2

/* Fetched past the count so the strip can pass over settled markets and still
   fill its slides with open ones. */
const HOME_HIGHLIGHT_FETCH_LIMIT = HOME_HIGHLIGHT_COUNT * 3

const HomeHighlight = lazy(() =>
  import('@/features/home-highlight').catch(err => {
    console.warn('Failed to load HomeHighlight component:', err)
    return { default: () => null }
  })
)

/* Lazy: the geographic map carries the state-boundary dataset, which only
   the midterm tag page should ever download. */
const MidtermMap = lazy(() =>
  import('@/features/midterm-map').catch(err => {
    console.warn('Failed to load MidtermMap component:', err)
    return { default: () => null }
  })
)

// ALTERNATIVE APPROACH: If dynamic imports continue to fail, you can replace the lazy imports above with these static imports:
/*
import HotTopic from '@/features/market/hot-topic'

// Then replace LazyComponentWrapper usage with:
// <HotTopic banners={banners} bannersLoading={bannersLoading} />
*/

// Define the breakpoints for column count and the multiplier for items per column
const BREAKPOINTS = { default: 5, 1899: 4, 1439: 3, 1024: 2, 981: 1 }
const BREAKPOINTS_WITH_SIDEBAR = { default: 4, 1899: 3, 1439: 2, 1024: 2, 981: 1 }

const getFutuurCoverUrl = () => `${import.meta.env.VITE_FUTUUR_STATIC_URL}/img/meta/futuur.com.png`

const ITEMS_PER_COLUMN = 8 // 4 columns × 8 items = 32 markets per load (increased from 24)

const ViewFeed = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const pathnameForFeed = useMemo(() => pathnameWithoutLocalePrefix(location.pathname), [location.pathname])
  const localePrefix = useMemo(() => getLocalePrefix(location.pathname), [location.pathname])
  /* `view` once picked the card/list layout on the URL; the choice now lives in storage,
     but links carrying the param still exist and it is not a feed filter, so it must not
     reach the questions endpoint. */
  const rawQueryParams = useMemo(() => {
    const filters = Object.fromEntries(new urlSearchParams(location.search))
    delete filters.view
    return filters
  }, [location.search])
  /* Hand-edited or stale links can carry values no control produces (an
     unknown ordering, `resolved_only=false`). Every consumer reads the
     sanitized copy, and the URL itself is canonicalized to it below, so the
     heading, the dropdowns and the request cannot disagree about the view. */
  const queryParams = useMemo(() => sanitizeFeedQueryParams(rawQueryParams), [rawQueryParams])

  /* Canonicalized from the URL itself, never from `queryParams`: that object
     is handed to FilterDropdownPanel, which writes its A/B default ordering
     into it, and a mutation no control performed must not rewrite the
     address bar (an A/B segment is not necessarily a valid ordering). */
  useEffect(() => {
    const raw = Object.fromEntries(new urlSearchParams(location.search))
    const canonical = sanitizeFeedQueryParams(raw)
    if (!feedQueryParamsChanged(raw, canonical)) return
    const query = new URLSearchParams(canonical).toString()
    navigate(`${location.pathname}${query ? `?${query}` : ''}`, { replace: true })
  }, [location.search, location.pathname, navigate])
  const searchTerm = queryParams.search || ''
  const urlTag = queryParams.tag || ''
  const currentBreakpoint = () => {
    if (typeof window === 'undefined') return

    const width = window.innerWidth
    let newBreakpoint = 'default'

    // Find the appropriate breakpoint based on window width
    const breakpointKeys = Object.keys(BREAKPOINTS)
      .filter(key => key !== 'default')
      .map(Number)
      .sort((a, b) => a - b) // Sort in ascending order
    for (const bp of breakpointKeys) {
      if (width <= bp) {
        newBreakpoint = bp
        break
      }
    }

    return BREAKPOINTS[newBreakpoint]
  }
  const [columnCount, setColumnCount] = useState(() =>
    typeof window === 'undefined' ? BREAKPOINTS.default : currentBreakpoint()
  )
  const [selectedTag, setSelectedTag] = useState(null)
  const [sideMenuOpen, setSideMenuOpen] = useState(true)
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false)
  const [sideMenuExpanded, setSideMenuExpanded] = useState({})
  const previousAuthStateRef = useRef(null)
  const hasEverRenderedFeedRef = useRef(false)
  const hasUsedPreloadedFeedRef = useRef(false)
  const prevCategoryIdRef = useRef(undefined)
  const prevSelectedTagRef = useRef(undefined)
  const prevSelectedFeaturedCategoryRef = useRef(undefined)
  const prevQueryParamsRef = useRef(undefined)
  const lastFetchKeyRef = useRef(null)
  /** Avoid duplicate fetchTagsList on hydrate when SSR already populated tags for this ordering. */
  const lastVirtualTagsOrderingKeyRef = useRef(null)

  const dispatch = useDispatch()
  const reduxStore = useStore()
  const currentUser = useCurrentUser()
  const activeMarketType = useSelector(selectActiveMarketType)
  const markets = useSelector(selectMarketRepository)
  const abTestsStatus = useSelector(selectAbTestsStatus)
  const pageLayout = useSelector(selectPageLayout)
  const feedIds = useSelector(selectFeedMarkets)
  const feedPagination = useSelector(selectFeedMarketsPagination)
  const feedQueryString = useSelector(state => state.markets.feed.queryString)
  const feedMarketType = useSelector(selectFeedMarketType)
  const error = useSelector(selectFeedMarketsError)
  const feedStatus = useSelector(selectFeedMarketsStatus)
  const banners = useSelector(selectFeedBanners)
  const bannersLoading = useSelector(selectFeedBannersStatus)
  const [marketSelected, setMarketSelected] = useState(null)
  const isMobileSize = useIsMobileSize()
  /* The highlight is a desktop-only surface: its two-column layout starts at
     the same 1024px breakpoint, and below it the strip added nothing. */
  const isBelowDesktop = useIsMobileSize(HOME_HIGHLIGHT_MIN_WIDTH - 1)
  const activeCurrency = useActiveCurrency()
  const mainCategories = useSelector(selectMainRootCategoryList)
  const categoriesStatus = useSelector(selectRootCategoriesStatus('ALL'))
  const activeLanguage = useSelector(selectActiveLanguage())
  // Get tag data if we have a selected tag

  const [categorySettings, setCategorySettings] = useState()
  const [categoryId, setCategoryId] = useState()
  const [rootCategoryId, setRootCategoryId] = useState()
  const categoryStatus = useSelector(selectCategoryStatusById(categoryId, activeMarketType))
  const currentSubcategory = useSelector(selectCategoryById(categoryId, activeMarketType))
  const rootCategory = useSelector(selectCategoryById(rootCategoryId))
  const subcategories = useSelector(selectCategoriesByParentId(categoryId, activeMarketType))
  const marketPath = parseMarketUrl(pathnameForFeed)
  const pathSlugs = (marketPath?.fullTree || '').split('/').filter(Boolean)
  const pathLeaf = parsePriceLeafSlug(pathSlugs[pathSlugs.length - 1])
  const nestedLeafUrl = Boolean(pathLeaf && marketPath?.currentCategoryId && pathSlugs.length > 1)
  const onPriceLeafPage = currentSubcategory
    ? Boolean(parsePriceLeafSlug(currentSubcategory.slug) && categoryParentId(currentSubcategory))
    : nestedLeafUrl
  /* Same id SSR filters on: the category in the URL, not the first slug.
     A nested page would otherwise group the ancestor's children. On a leaf
     the sidebar still belongs to the parent. */
  const priceMenuCategoryId = onPriceLeafPage
    ? categoryParentId(currentSubcategory) || rootCategoryId || categoryId
    : categoryId
  const priceMenuChildren = useSelector(selectCategoriesByParentId(priceMenuCategoryId, activeMarketType))
  const priceMenuStatus = useSelector(selectCategoryStatusById(priceMenuCategoryId, activeMarketType))
  const priceParentCategory = useSelector(selectCategoryById(priceMenuCategoryId))
  const priceChildrenAreLeaves = isCryptoPriceCategory(priceMenuChildren)
  const priceMenuPending =
    priceMenuStatus === constants.IDLE || priceMenuStatus === constants.LOADING
  const showCryptoPriceMenu = priceChildrenAreLeaves || (onPriceLeafPage && priceMenuPending)
  const selectedPriceWindow = onPriceLeafPage
    ? (parsePriceLeafSlug(currentSubcategory?.slug) || pathLeaf)?.window || null
    : queryParams.window || null
  const selectedPriceAsset = onPriceLeafPage
    ? (parsePriceLeafSlug(currentSubcategory?.slug) || pathLeaf)?.asset || null
    : queryParams.asset || null
  const priceFacetIds =
    priceChildrenAreLeaves && !onPriceLeafPage && (queryParams.window || queryParams.asset)
      ? cryptoPriceCategoryIds(priceMenuChildren, {
          window: queryParams.window,
          asset: queryParams.asset,
        })
      : null
  const priceFacetKey = priceFacetIds == null ? '' : priceFacetIds.join(',')
  const deferPriceFacetFetch =
    Boolean(queryParams.window || queryParams.asset) &&
    !onPriceLeafPage &&
    Boolean(categoryId) &&
    priceMenuPending
  const tagList = useSelector(selectTagsList)
  const tagsStatus = useSelector(selectTagsListStatus())
  const featuredCategories = useSelector(selectFeaturedCategories())
  const featuredCategoriesStatus = useSelector(selectFeaturedCategoriesStatus())
  const searchedTags = useSelector(selectPageTags())
  const searchedTagsStatus = useSelector(selectPageTagsStatus())
  /* The "All" view's featured-topic chip is URL state (`?topic=<slug>`), like
     every other feed filter: it survives reload and sharing, and the back
     button undoes it. It only applies on the volume view without a search
     term — anywhere else the param is cleaned off the URL below. The slug
     resolves to the category id the questions endpoint filters by; a numeric
     topic (an older link) is used as the id directly. */
  const urlTopic = queryParams.topic || ''
  const cleanPathForTopic = pathnameForFeed.replace(/\/$/, '')
  const isTopicView =
    (cleanPathForTopic === '' || cleanPathForTopic === '/markets') &&
    !categoryId &&
    queryParams.ordering === '-volume' &&
    !searchTerm
  /* Guarded on a non-empty topic: with no `?topic=` in the URL, `''` would
     otherwise match any featured category that has no slug and silently
     filter the default All feed. */
  const topicCategory =
    isTopicView && urlTopic
      ? (featuredCategories || []).find(
          category => String(category.slug || '') === urlTopic || String(category.id) === String(urlTopic)
        )
      : undefined
  const selectedFeaturedCategory =
    isTopicView && urlTopic ? (topicCategory?.id ?? (/^\d+$/.test(urlTopic) ? urlTopic : null)) : null
  const selectedTopicValue = topicCategory?.slug || topicCategory?.id || urlTopic
  /* Read off the path rather than off `selectedTag`, which an effect fills in a
     render later — the heading would flash empty first. */
  const pathTagSlug = useMemo(() => parseTagUrl(pathnameForFeed)?.tagSlug || '', [pathnameForFeed])
  const pathTag = useSelector(selectTempTagBySlug(pathTagSlug))

  function parseTagUrl(url) {
    // Remove leading and trailing slashes
    let cleanedUrl = url.replace(/^\/|\/$/g, '')

    // If the URL is empty, return null
    if (cleanedUrl === '') {
      return null
    }

    // Split the URL into parts
    let parts = cleanedUrl.split('/')

    // Ensure it starts with "tags"
    if (parts[0] !== 'tags') {
      return null
    }

    // Remove "tags" from the array
    parts.shift()

    // If we have at least a tag slug
    if (parts && parts.length > 0) {
      const tagSlug = parts[0]
      const tagId = parts.length > 1 ? parts[1] : null

      return {
        tagSlug,
        tagId,
      }
    }

    return null
  }

  function parseMarketUrl(url) {
    // Remove leading and trailing slashes
    let cleanedUrl = url.replace(/^\/|\/$/g, '')

    // If the URL is empty, return null
    if (cleanedUrl === '') {
      return null
    }

    if (cleanedUrl === 'markets')
      return {
        category: null, // First category if exists, otherwise null
        fullTree: '/markets', // Full path, even for one category
        currentCategoryId: null, // No specific category selected, just /markets root
      }

    // Split the URL into parts
    let parts = cleanedUrl.split('/')

    // Ensure it starts with "markets"
    if (parts[0] !== 'markets') {
      return null
    }

    // Remove "markets" from the array
    parts.shift()

    // If the last segment is a number, treat it as categoryId
    let currentCategoryId = isNaN(parts[parts.length - 1]) ? null : parts.pop()
    let fullTree = parts.join('/')

    return {
      category: parts[0] || null, // First category if exists, otherwise null
      fullTree: fullTree || null, // Full path, even for one category
      currentCategoryId: currentCategoryId, // Last segment if it's a number
    }
  }
  const fetchMarkets = (shouldResetFeed = false) => {
    // Always fetch for desktop column count (4 columns) regardless of screen size
    // This ensures mobile users get the same amount of initial content as desktop users
    const desktopColumnCount = BREAKPOINTS.default || 4
    const limit = desktopColumnCount * ITEMS_PER_COLUMN

    const currentQueryParams = queryParams
    const options = { limit, ...currentQueryParams }
    /* `topic` names the featured category in the page URL; the endpoint knows
       it as `categories` (set below), so the slug itself never goes upstream.
       An ordering that is not a real option (only reachable by mutating the
       shared params object after the URL was sanitized) is dropped rather
       than sent as an empty or unknown value. `window` and `asset` are the
       Crypto Price sidebar; they become category ids below. */
    delete options.topic
    if (!isValidFeedOrdering(options.ordering)) delete options.ordering
    delete options.window
    delete options.asset

    /* Trending on the root, closing-soonest in sports — whichever the section opens on
       when the URL names no ordering. Remove trailing slash for comparison. */
    const cleanPath = pathnameForFeed.replace(/\/$/, '')
    const isOnRoot = cleanPath === '' || cleanPath === '/markets'
    if (
      !currentQueryParams.ordering &&
      !selectedTag &&
      (isOnRoot ? !categoryId : isSportsCategoryPath(pathnameForFeed))
    ) {
      options.ordering = defaultFeedOrdering(pathnameForFeed)
    }

    if (categoryId) options.categories = categoryId
    if (selectedFeaturedCategory) options.categories = selectedFeaturedCategory
    if (priceFacetIds != null) options.categories = priceFacetIds.join(',')
    if (selectedTag) options.tag = selectedTag

    dispatch(fetchFeed(activeMarketType, options, location.search, shouldResetFeed))
  }
  useEffect(() => {
    if (!pageLayout.appLoading) {
      dispatch(fetchFeedBanners(activeMarketType))
    }
  }, [pageLayout.appLoading, activeMarketType])

  // NOTE: do NOT preload `hot-topic` here. It is gated by `react-lazyload`,
  // which triggers the import once the user scrolls it into range.

  useEffect(() => {
    if (pathnameForFeed.indexOf('/tags') > -1) {
      if (tagsStatus === constants.IDLE || tagsStatus === constants.ERROR) {
        dispatch(fetchTagsList({ currency_mode: activeMarketType }))
      }

      // Parse tag from URL
      const tagSettings = parseTagUrl(pathnameForFeed)

      if (tagSettings) {
        setSelectedTag(tagSettings.tagSlug)
        // Fetch tag data if needed
        if (tagSettings.tagSlug) {
          dispatch(getTagName(tagSettings.tagSlug))
        }
      } else {
        setSelectedTag(null)
      }

      return
    }

    // Off the tag pages the topic filter lives in `?tag=` instead
    setSelectedTag(urlTag || null)
  }, [pathnameForFeed, tagsStatus, urlTag, dispatch])

  // A new search starts from "All": the topics strip is rebuilt for the new
  // term, so a refinement chosen for the previous one no longer belongs to it.
  // The topic lives in the URL now, so starting a search removes the param
  // instead of clearing state (see the ordering cleanup below, which is the
  // same rule for leaving the "All" view).
  useEffect(() => {
    if (!urlTopic) return
    if (!searchTerm && queryParams.ordering === '-volume') return
    /* Same source as the canonicalizer above (the sanitized URL), so the two
       cleanups cannot replace each other's result in the same commit. */
    const raw = Object.fromEntries(new urlSearchParams(location.search))
    const params = new URLSearchParams(sanitizeFeedQueryParams(raw))
    params.delete('topic')
    const query = params.toString()
    navigate(`${location.pathname}${query ? `?${query}` : ''}`, { replace: true })
  }, [searchTerm, queryParams.ordering, urlTopic, location.pathname, location.search, navigate])

  // On the feed root the topic refinement is entirely URL state: `?tag=<slug>`
  // both selects the chip and filters the feed, so it survives reload, sharing
  // and the back button. `/tags/<slug>` keeps its own path-driven selection.
  useEffect(() => {
    const cleanPath = pathnameForFeed.replace(/\/$/, '')
    const isOnRoot = cleanPath === '' || cleanPath === '/markets'
    if (!isOnRoot) return

    setSelectedTag(urlTag || null)
  }, [pathnameForFeed, urlTag])

  // (Leaving "All" removes `?topic=` in the search effect above: the topic
  // only exists on the volume view, and the panel drops it when it changes
  // the ordering, so the two cleanups agree.)

  // Fetch tags for Trending and Newest virtual categories (not "All" which shows featured categories)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const orderingParam = searchParams.get('ordering')
    const orderingKey = orderingParam === '-created_on' ? '-created_on' : 'default'
    const cleanPath = pathnameForFeed.replace(/\/$/, '')
    const isOnRoot = cleanPath === '' || cleanPath === '/markets'

    if (!isOnRoot || categoryId || pathnameForFeed.indexOf('/tags') > -1 || orderingParam === '-volume') {
      return
    }

    if (tagsStatus === constants.LOADING) return

    // Hydration: SSR already fetched tags — record ordering and skip one duplicate client fetch.
    if (
      tagsStatus === constants.COMPLETE &&
      lastVirtualTagsOrderingKeyRef.current === null &&
      typeof window !== 'undefined' &&
      window.__PRELOADED_STATE__
    ) {
      lastVirtualTagsOrderingKeyRef.current = orderingKey
      return
    }

    if (
      tagsStatus === constants.COMPLETE &&
      lastVirtualTagsOrderingKeyRef.current === orderingKey
    ) {
      return
    }

    if (isOnRoot && !categoryId && pathnameForFeed.indexOf('/tags') === -1 && orderingParam !== '-volume') {
      if (orderingParam === '-created_on') {
        lastVirtualTagsOrderingKeyRef.current = '-created_on'
        dispatch(fetchTagsList({ ordering: '-created_on', currency_mode: activeMarketType }))
      } else if (!orderingParam || orderingParam === '-relevance') {
        lastVirtualTagsOrderingKeyRef.current = 'default'
        dispatch(fetchTagsList({ currency_mode: activeMarketType }))
      }
    }
  }, [pathnameForFeed, location.search, categoryId, activeMarketType, dispatch, tagsStatus])

  useEffect(() => {
    const categorySettings = parseMarketUrl(pathnameForFeed)
    if (!categorySettings) {
      setCategoryId(null)
      setCategorySettings(null)
      return
    }

    setCategorySettings(categorySettings)
    const mainCat = categorySettings.category
      ? mainCategories.find(c => c.slug === categorySettings.category)
      : null

    if (categorySettings.currentCategoryId) {
      setCategoryId(categorySettings.currentCategoryId)
      setRootCategoryId(mainCat?.id || null)
    } else if (mainCat) {
      setCategoryId(mainCat.id)
      setRootCategoryId(mainCat.id)
    } else {
      setCategoryId(null)
      setRootCategoryId(null)
    }
  }, [pathnameForFeed, mainCategories])

  useEffect(() => {
    if (pageLayout.appLoading || categoryId === undefined) return
    if (deferPriceFacetFetch) return
    const queryParamsKey = JSON.stringify(queryParams)
    /* SSR already requested this exact URL (including ordering, search, and tag).
       Painting that list beats resetting it and waiting on the same request again. */
    const preloadedFeedMatches =
      typeof window !== 'undefined' &&
      window.__PRELOADED_STATE__ &&
      feedStatus === constants.COMPLETE &&
      feedIds?.length > 0 &&
      feedQueryString === location.search &&
      String(feedMarketType ?? '') === String(activeMarketType ?? '')
    if (preloadedFeedMatches && !hasUsedPreloadedFeedRef.current) {
      hasUsedPreloadedFeedRef.current = true
      prevCategoryIdRef.current = categoryId
      prevSelectedTagRef.current = selectedTag
      prevSelectedFeaturedCategoryRef.current = selectedFeaturedCategory
      prevQueryParamsRef.current = queryParamsKey
      lastFetchKeyRef.current = `${activeMarketType}|${categoryId}|${selectedTag}|${selectedFeaturedCategory}|${queryParamsKey}|${priceFacetKey}`
      return
    }
    hasUsedPreloadedFeedRef.current = true
    const categoryChanged = prevCategoryIdRef.current !== undefined && prevCategoryIdRef.current !== categoryId
    const tagChanged = prevSelectedTagRef.current !== undefined && prevSelectedTagRef.current !== selectedTag
    const featuredChanged = prevSelectedFeaturedCategoryRef.current !== undefined && prevSelectedFeaturedCategoryRef.current !== selectedFeaturedCategory
    const queryChanged = prevQueryParamsRef.current !== undefined && prevQueryParamsRef.current !== queryParamsKey
    prevCategoryIdRef.current = categoryId
    prevSelectedTagRef.current = selectedTag
    prevSelectedFeaturedCategoryRef.current = selectedFeaturedCategory
    prevQueryParamsRef.current = queryParamsKey

    const fetchKey = `${activeMarketType}|${categoryId}|${selectedTag}|${selectedFeaturedCategory}|${queryParamsKey}|${priceFacetKey}`
    const somethingChanged = categoryChanged || tagChanged || featuredChanged || queryChanged
    if (!somethingChanged && lastFetchKeyRef.current === fetchKey) return
    lastFetchKeyRef.current = fetchKey

    const shouldResetFeed = !feedIds?.length || somethingChanged || feedQueryString !== location.search
    fetchMarkets(shouldResetFeed)
  }, [pageLayout.appLoading, activeMarketType, location.search, categoryId, selectedTag, selectedFeaturedCategory, feedStatus, feedQueryString, feedMarketType, deferPriceFacetFetch, priceFacetKey])

  useEffect(() => {
    if (pathnameForFeed.includes('/manifest')) addCampain('manifest')
  }, [])

  useEffect(() => {
    if (categoryId && categoryStatus === constants.IDLE) {
      dispatch(fetchCategories(categoryId, activeMarketType))
    }
  }, [activeMarketType, categoryId, categoryStatus])

  useEffect(() => {
    if (
      priceMenuCategoryId &&
      String(priceMenuCategoryId) !== String(categoryId) &&
      priceMenuStatus === constants.IDLE
    ) {
      dispatch(fetchCategories(priceMenuCategoryId, activeMarketType))
    }
  }, [activeMarketType, categoryId, priceMenuCategoryId, priceMenuStatus, dispatch])

  // Build expanded accordion state for the side menu based on current category's ancestor chain
  useEffect(() => {
    const rootId = rootCategoryId || categoryId
    if (!categoryId || !rootId || String(categoryId) === String(rootId)) {
      return
    }
    const pool = reduxStore.getState().categories.pool.data
    const active = pool[categoryId]
    if (!active) return

    let expanded = {}

    // Try tree property first
    if (active.tree && active.tree.length > 1) {
      const rootIdx = active.tree.findIndex(n => String(n.id) === String(rootId))
      if (rootIdx !== -1) {
        for (let i = rootIdx; i < active.tree.length - 1; i++) {
          expanded[active.tree[i].id] = active.tree[i + 1].id
        }
      }
    }

    // Fallback: walk parent chain
    if (Object.keys(expanded).length === 0) {
      const chain = []
      let cur = active
      const visited = new Set()
      while (cur && !visited.has(cur.id)) {
        visited.add(cur.id)
        chain.unshift(cur.id)
        if (String(cur.id) === String(rootId)) break
        cur = cur.parent ? pool[cur.parent] : null
      }
      if (chain.length >= 2 && String(chain[0]) === String(rootId)) {
        for (let i = 0; i < chain.length - 1; i++) {
          expanded[chain[i]] = chain[i + 1]
        }
      }
    }

    if (Object.keys(expanded).length > 0) {
      setSideMenuExpanded(expanded)
    }
  }, [categoryId, rootCategoryId, currentSubcategory])

  const lastMainRootCategoriesFetchMarketTypeRef = useRef(null)
  const lastFeaturedSearchRef = useRef(undefined)

  useEffect(() => {
    if (pageLayout.appLoading) return

    const marketTypeChanged = lastMainRootCategoriesFetchMarketTypeRef.current !== activeMarketType
    const shouldFetchMainRootCategories =
      categoriesStatus === constants.IDLE || categoriesStatus === constants.ERROR || marketTypeChanged

    if (shouldFetchMainRootCategories && categoriesStatus !== constants.LOADING) {
      lastMainRootCategoriesFetchMarketTypeRef.current = activeMarketType
      dispatch(fetchMainRootCategories(activeMarketType))
    }
  }, [pageLayout.appLoading, activeMarketType, categoriesStatus])

  useEffect(() => {
    if (pageLayout.appLoading) return

    const searchChanged = lastFeaturedSearchRef.current !== searchTerm
    const shouldFetch =
      featuredCategoriesStatus === constants.IDLE || searchChanged

    if (shouldFetch && featuredCategoriesStatus !== constants.LOADING) {
      lastFeaturedSearchRef.current = searchTerm
      dispatch(fetchFeaturedCategories(activeMarketType, { search: queryParams.search }))
    }
  }, [pageLayout.appLoading, activeMarketType, queryParams.search])

  // On the search results page the topics strip lists the tags that match the
  // searched term, not the feed's trending tags. It reads its own slot, so the
  // navbar dropdown searching as the user types cannot rewrite it.
  useEffect(() => {
    if (pageLayout.appLoading) return
    if (!searchTerm) return

    dispatch(searchPageTags(searchTerm, { currencyId: activeCurrency.id }))
  }, [pageLayout.appLoading, searchTerm, activeMarketType, activeCurrency.id])

  /** Rewrites the current URL with one param changed, dropping it when empty. */
  const navigateWithParam = (key, value) => {
    const params = new URLSearchParams(location.search)
    if (value) params.set(key, value)
    else params.delete(key)
    const query = params.toString()
    navigate(`${location.pathname}${query ? `?${query}` : ''}`)
  }

  /* Leaves the term and its refinement behind but stays where it is: clearing a
     search inside a category keeps you in that category. On `/tags/<slug>` the
     tag is the path itself, so there is nothing to stay on — that one lands on
     the markets feed. The navbar input reads the term off the URL, so it empties
     itself. */
  const handleClearHeading = () => {
    if (pathTagSlug) {
      navigate(`${localePrefix}${marketsUrl()}`)
      return
    }
    const params = new URLSearchParams(location.search)
    params.delete('search')
    params.delete('tag')
    params.delete('topic')
    const query = params.toString()
    navigate(`${location.pathname}${query ? `?${query}` : ''}`)
  }

  /* Refining is URL state, not component state: the chip narrows what is already
     on screen and keeps the term, the ordering and every other param in place —
     so the narrowed view is shareable and the back button undoes it. */
  const handleTopicRefine = tagSlug => navigateWithParam('tag', tagSlug)

  /* The "All" view's featured categories are URL state too (`?topic=<slug>`),
     for the same reason: a category picked there survives reload and sharing
     instead of living in state that any sort change silently cleared. */
  const handleFeaturedTopic = topicValue => navigateWithParam('topic', topicValue)

  /* Applies the status filter over whatever is already on screen — the tag and
     every other active param stay put. The three status filters cancel each
     other out, same as in the filter panel. */
  const handleViewClosedEvents = () => {
    const params = new URLSearchParams(location.search)
    params.set('resolved_only', 'true')
    params.delete('live')
    params.delete('live_soon')
    params.delete('pending_resolution')
    navigate(`${location.pathname}?${params.toString()}`)
  }

  // Handle column count changes
  const handleBreakpointChange = newColumnCount => {
    if (newColumnCount !== columnCount) {
      setColumnCount(newColumnCount)
    }
  }

  const renderHelmet = () => {
    // Default Feed Helmet (for / or /markets)
    // Remove trailing slash for comparison
    const cleanPath = pathnameForFeed.replace(/\/$/, '')
    /* Results pages are visitor-generated and their canonical is `/markets/`
       anyway — keep them out of the index, but let the links be crawled. */
    if (searchTerm) {
      return (
        <Helmet
          id="search-helmet"
          follow={false}
          title={i18n('search.meta_tags.title', { term: searchTerm })}
          description={i18n('search.meta_tags.description', { term: searchTerm })}
          images={getFutuurCoverUrl()}
          url={shareService.shareableUrl(marketsUrl())}
        />
      )
    }
    if (cleanPath === '' || cleanPath === '/markets') {
      const props = {
        title: i18n('landing.meta_tags.title'),
        description: i18n('landing.meta_tags.description', { marketCount: feedPagination.total }),
        images: getFutuurCoverUrl(),
        url: shareService.shareableUrl(feedUrl()),
      }
      return <Helmet id="markets-helmet" {...props} />
    }

    // Unknown /markets/… slug after categories loaded — do not self-canonicalize.
    if (
      pathnameForFeed.startsWith('/markets/') &&
      categorySettings?.category &&
      !categoryId &&
      mainCategories?.length > 0
    ) {
      return (
        <Helmet
          id="markets-not-found-helmet"
          follow={false}
          omitCanonical
          title={i18n('not_found.title')}
          description={i18n('not_found.description')}
          images={getFutuurCoverUrl()}
        />
      )
    }

    // Category Helmet (for /markets/something)
    if (pathnameForFeed.startsWith('/markets/') && categoryId && isWorldCup) {
      return (
        <Helmet
          id="world-cup-helmet"
          title={i18n('worldcup.meta_title')}
          description={i18n('worldcup.meta_description')}
          images={worldCupCoverUrl() || getFutuurCoverUrl()}
          url={shareService.shareableUrl(location.pathname)}
        />
      )
    }

    if (pathnameForFeed.startsWith('/markets/') && categoryId && currentSubcategory) {
      const categoryName = currentSubcategory.title || currentSubcategory.slug || 'Category'
      const categoryMeta = getCategorySeoMeta({
        categoryName,
        marketCount: feedPagination?.total,
        isPtBr: activeLanguage === 'pt-br',
        slug: currentSubcategory.slug || categoryName,
      })
      let helmetProps = {
        title: categoryMeta.title,
        description: categoryMeta.description,
        images: (banners && banners.length ? banners[0].image : '') || getFutuurCoverUrl(),
        url: shareService.shareableUrl(categoryListUrl(currentSubcategory)),
      }

      if (currentSubcategory.seo) {
        const { seo } = currentSubcategory
        const language = activeLanguage === 'pt-br' ? 'pt_br' : 'en'
        const seoTitle = seo[`seo_title_${language}`]
        const seoDescription = seo[`seo_description_${language}`]
        helmetProps = {
          images: seo.seo_image || getFutuurCoverUrl(),
          url: shareService.shareableUrl(categoryListUrl(currentSubcategory)),
          description: seoDescription || categoryMeta.description,
          title: seoTitle
            ? seoTitle.includes('Futuur')
              ? seoTitle
              : `${seoTitle} Forecasts and Predictions | Futuur`
            : categoryMeta.title,
          alt: seo[`seo_alt_${language}`],
        }
      }

      return <Helmet id="category-helmet" {...helmetProps} />
    }

    // Tags Helmet (for /tags/something)
    if (pathnameForFeed.startsWith('/tags') && selectedTag) {
      const tagKnown = (tagList || []).some(t => {
        const slug = (t.slug || t.name || '').toLowerCase()
        return slug === String(selectedTag).toLowerCase()
      })
      const hasFeedResults = (feedIds && feedIds.length > 0) || (feedPagination && feedPagination.total > 0)
      if ((tagList || []).length > 0 && !tagKnown && !hasFeedResults) {
        return (
          <Helmet
            id="tags-not-found-helmet"
            follow={false}
            omitCanonical
            title={i18n('not_found.title')}
            description={i18n('not_found.description')}
            images={getFutuurCoverUrl()}
          />
        )
      }
      return (
        <Helmet
          id="tags-helmet"
          title={i18n('tag.meta_tags.title', { tag: pathTagName || selectedTag })}
          description={i18n('tag.meta_tags.description', {
            tag: pathTagName || selectedTag,
            marketCount: (feedPagination && feedPagination.total) || 50,
          })}
          images={getFutuurCoverUrl()}
          url={shareService.shareableUrl(location.pathname)}
        />
      )
    }

    // Default fallback
    const props = {
      title: i18n('landing.meta_tags.title'),
      description: i18n('landing.meta_tags.description', { marketCount: feedPagination.total }),
      images: getFutuurCoverUrl(),
      url: shareService.shareableUrl(feedUrl()),
    }
    return <Helmet id="markets-helmet" {...props} />
  }
  /* we may use this for showing a custom category */
  // const renderCustomCategory = () => {
  //   return {}
  // }

  // Check if we're on a virtual category (Trending, Newest, All)
  const isOnVirtualCategory = () => {
    const searchParams = new URLSearchParams(location.search)
    const orderingParam = searchParams.get('ordering')
    const cleanPath = pathnameForFeed.replace(/\/$/, '')
    const isOnRoot = cleanPath === '' || cleanPath === '/markets'
    return isOnRoot && !categoryId && (orderingParam === '-created_on' || !orderingParam || orderingParam === '-relevance' || orderingParam === '-volume')
  }

  const isOnAllCategory = () => {
    const searchParams = new URLSearchParams(location.search)
    const orderingParam = searchParams.get('ordering')
    const cleanPath = pathnameForFeed.replace(/\/$/, '')
    const isOnRoot = cleanPath === '' || cleanPath === '/markets'
    return isOnRoot && !categoryId && orderingParam === '-volume'
  }

  const isWorldCup = isWorldCupCategory({ categoryId, pathname: pathnameForFeed })

  // Determine which subcategories to show
  const getSubcategories = () => {
    if (searchTerm) {
      return withUrlTagChip(searchedTags.map(t => ({ ...t, title: t.name })))
    }

    /* On a tag's own page the tag is already the subject of the page — a strip of
       every other tag under the category row is navigation, not context. */
    if (pathTagSlug) return []

    if (pathnameForFeed.indexOf('/tags') > -1) {
      return tagList.map(t => ({ ...t, title: t.name }))
    }

    // "All" virtual category shows featured categories
    if (isOnAllCategory()) {
      return featuredCategories ?? []
    }

    // Trending / New virtual categories show tags
    if (isOnVirtualCategory()) {
      return withUrlTagChip(tagList.map(t => ({ ...t, title: t.name })))
    }

    return subcategories
  }

  /* A topic opened from the search dropdown leads the row when the list does not
     carry it. A topic the list already carries keeps its own place, so selecting
     a chip never moves the chips around under the cursor. */
  const withUrlTagChip = topics => {
    if (!urlTag) return topics
    if (topics.some(topic => topic.slug === urlTag)) return topics

    return [{ id: `url-tag-${urlTag}`, slug: urlTag, name: urlTagName, title: urlTagName }, ...topics]
  }

  // Determine the fullPath for navigation links (stay under `/pt-br/...` when locale URL is active)
  const getFullPath = () => {
    let base
    if (searchTerm) {
      base = '/tags'
    } else if (pathnameForFeed.indexOf('/tags') > -1) {
      base = '/tags'
    } else if (isOnAllCategory()) {
      base = '/markets'
    } else if (isOnVirtualCategory()) {
      base = '/tags'
    } else {
      base = categorySettings?.fullTree
    }
    if (!localePrefix) return base
    if (base === '/tags') return `${localePrefix}/tags`
    if (base === '/markets') return `${localePrefix}/markets`
    return base
  }

  const isCategoryPage = !!categoryId && !isOnVirtualCategory()
  const hasSideMenuContent = isCategoryPage && !isWorldCup
  const showSideMenu = hasSideMenuContent && sideMenuOpen && !isMobileSize

  const openPriceFacet = ({ window, asset }) => {
    const params = new urlSearchParams(location.search)
    if (window) params.set('window', window)
    else params.delete('window')
    if (asset) params.set('asset', asset)
    else params.delete('asset')
    const qs = params.toString()
    const stayingOnParent =
      priceParentCategory && String(categoryId) === String(priceParentCategory.id)
    /* A breadcrumb array is `/markets/<slug>/<id>`. A bare object is
       `/markets/<id>/<slug>`, and the feed then reads no category id. */
    const path = stayingOnParent
      ? location.pathname
      : priceParentCategory
        ? `${localePrefix}${categoryListUrl([priceParentCategory])}`
        : location.pathname
    navigate(`${path}${qs ? `?${qs}` : ''}`)
    setMobileSheetOpen(false)
  }

  const renderCategorySideMenu = ({ mobile = false } = {}) => {
    if (showCryptoPriceMenu) {
      return (
        <CryptoPriceSideMenu
          categoryId={priceMenuCategoryId}
          activeMarketType={activeMarketType}
          selectedWindow={selectedPriceWindow}
          selectedAsset={selectedPriceAsset}
          onSelect={openPriceFacet}
          onCollapse={mobile ? undefined : () => setSideMenuOpen(false)}
          showHeader={!mobile}
          className={mobile ? 'side-menu--mobile' : ''}
        />
      )
    }
    return (
      <CategoriesSideMenuNavigation
        categoryId={rootCategoryId || categoryId}
        currentSubcategoryId={categoryId}
        activeMarketType={activeMarketType}
        onClose={() => (mobile ? setMobileSheetOpen(false) : setSideMenuOpen(false))}
        onCollapse={mobile ? undefined : () => setSideMenuOpen(false)}
        onSelect={mobile ? () => setMobileSheetOpen(false) : undefined}
        isCollapsible={!mobile}
        showHeader={!mobile}
        title={rootCategory?.title || currentSubcategory?.title || ''}
        parentCategory={rootCategory || currentSubcategory}
        className={mobile ? 'side-menu--mobile' : ''}
        expandedByParent={sideMenuExpanded}
        onExpandedChange={setSideMenuExpanded}
      />
    )
  }

  const isHomeFeed = pathnameForFeed === feedUrl()
  const showHomeHighlight = isHomeFeed && !currentSubcategory && !isBelowDesktop

  /* On desktop the event opens the hero strip. Below that breakpoint the strip
     is gone, so the event goes into the feed as a card the size of a market
     card, a few cards down. With no open Senate market the insert is dropped
     here rather than left to the banner: the grid wraps the insert in a slot of
     its own, which would sit empty around a banner rendering nothing. */
  const { hidden: midtermHidden } = useSenateControl()
  const feedInsert = useMemo(() => {
    if (midtermHidden || !isHomeFeed || currentSubcategory || !isBelowDesktop) return null
    return { afterRows: columnCount <= 1 ? 3 : 1, element: <MidtermBanner /> }
  }, [midtermHidden, isHomeFeed, currentSubcategory, isBelowDesktop, columnCount])

  /* The strip holds the leading trending markets, which is what it promises
     whatever the grid below is filtered to. It carries its own list rather than
     reading the feed's: the feed is refetched by every chip, and a strip that
     followed it would reload three charts each time. `markets` is not read
     either — `resetFeed` empties the pool on each of those refetches. */
  const [heroMarkets, setHeroMarkets] = useState([])
  const [heroStatus, setHeroStatus] = useState(constants.IDLE)

  useEffect(() => {
    if (!showHomeHighlight) return undefined

    let active = true
    setHeroStatus(constants.LOADING)
    questionService
      .getAll({ ordering: '-relevance', limit: HOME_HIGHLIGHT_FETCH_LIMIT, currency_mode: activeMarketType })
      .then(payload => {
        if (!active) return
        setHeroMarkets(payload?.results ?? [])
        setHeroStatus(constants.COMPLETE)
      })
      /* The strip opens on the event either way, so a failed fetch costs the
         market slides and nothing else. */
      .catch(() => {
        if (active) setHeroStatus(constants.ERROR)
      })

    return () => {
      active = false
    }
  }, [showHomeHighlight, activeMarketType])

  const { highlightMarkets, highlightedIds } = useMemo(() => {
    if (!showHomeHighlight) return { highlightMarkets: [], highlightedIds: new Set() }

    const picked = []
    const ids = new Set()

    /* Unsettled markets lead, in the order they came; settled ones only fill a
       slide nothing open was left for. Same notion of settled as the adapter
       applies to outcomes, so the strip's order and each slide's list agree. */
    const ordered = [...heroMarkets].sort(
      (a, b) => Number(isSettledStatus(a.status)) - Number(isSettledStatus(b.status))
    )

    ordered.forEach(market => {
      if (picked.length >= HOME_HIGHLIGHT_COUNT) return

      const featured = toFeaturedMarket(market, { currencyId: activeCurrency.id })
      if (!featured) return

      picked.push(featured)
      ids.add(market.id)
    })

    return { highlightMarkets: picked, highlightedIds: ids }
  }, [showHomeHighlight, heroMarkets, activeCurrency.id])

  /* Whatever the strip promoted is dropped from the grid, so the same market is
     never on screen twice. A market the adapter could not read was not promoted,
     so it stays in the grid rather than going missing from both. */
  const feedMarkets = useMemo(
    () =>
      feedIds
        .filter(id => !highlightedIds.has(id))
        .map(id => markets[id])
        .filter(Boolean),
    [feedIds, highlightedIds, markets]
  )
  /* What the grid actually dropped: a filtered feed need not carry the strip's
     markets at all. */
  const hiddenRowsCount = feedIds.length - feedMarkets.length
  // Home and every virtual tab (/ and /markets — Trending, New, All) render the
  // tags strip below the H1 instead of inside CategoryNavigation
  const showTagsBelowHeader = isHomeFeed || isOnVirtualCategory()
  /* Resolved against the lists already in the store, falling back to the slug
     itself — a heading is never worth a request of its own. */
  const tagNameForSlug = slug =>
    [...tagList, ...searchedTags].find(t => t.slug === slug)?.name ||
    slug.replace(/-/g, ' ').replace(/(^|\s)\S/g, character => character.toUpperCase())
  const urlTagName = urlTag ? tagNameForSlug(urlTag) : ''
  /* On a tag's own page the tag IS the page, so it names the H1. `getTagName`
     fetches the canonical casing; until it lands the slug reads well enough. */
  const pathTagName = pathTagSlug ? pathTag?.name || tagNameForSlug(pathTagSlug) : ''
  const tagsRowLoading = searchTerm
    ? searchedTagsStatus === constants.LOADING
    : isOnVirtualCategory() &&
      (isOnAllCategory() ? featuredCategoriesStatus === constants.LOADING : tagsStatus === constants.LOADING)
  const tagsRow = (
    <HomeTagsRow
      subcategories={getSubcategories()}
      loading={tagsRowLoading}
      fullPath={getFullPath()}
      onTagSelect={isOnAllCategory() && !searchTerm ? handleFeaturedTopic : handleTopicRefine}
      activeTag={isOnAllCategory() && !searchTerm ? selectedTopicValue : selectedTag}
      /* A `?tag=` refinement is invisible on "All", whose strip lists featured
         categories instead of tags: a shared link carrying one filtered the
         grid with nothing on screen saying so, and no way to clear it. The
         chip names it and clears it, next to the featured topics it refines. */
      refinementChip={
        urlTag && isOnAllCategory() && !searchTerm
          ? { value: urlTag, text: urlTagName, onClear: () => handleTopicRefine(null) }
          : null
      }
      /* On `/tags/<slug>` the tag IS the subject of the page — there is no "All"
         to fall back to, the header's X leaves it. A `?tag=` on the feed root is
         only a refinement, so "All" stays as the way back to all results. */
      showAllChip={!pathTagSlug}
    />
  )
  /* A search, tag, category or subcategory with nothing open still has history
     worth reading, so an empty feed points at the closed events instead of
     dead-ending. Any other empty feed keeps the clear-filters message, which
     doesn't drop the filters the user set. Not offered when closed events are
     already what is listed. */
  const showClosedEventsEmptyState =
    Boolean(searchTerm || urlTag || pathTagSlug || categoryId) &&
    !queryParams.resolved_only &&
    feedStatus === constants.COMPLETE &&
    (!feedIds || feedIds.length === 0)
  const activeBreakpoints = showSideMenu ? BREAKPOINTS_WITH_SIDEBAR : BREAKPOINTS

  const classList = classnames('view-feed', {
    'view-feed--authenticated': Boolean(currentUser),
    'view-feed--loading': feedStatus === constants.LOADING,
  })

  // Once the feed has rendered, filter/tag changes only reload the grid (card
  // skeletons inside InfiniteGrid) — never the full-page skeleton again.
  if (feedStatus === constants.COMPLETE && feedIds && feedIds.length > 0) {
    hasEverRenderedFeedRef.current = true
  }
  const showSkeleton =
    !hasEverRenderedFeedRef.current &&
    (feedStatus === constants.LOADING || feedStatus === constants.IDLE) &&
    (!feedIds || feedIds.length === 0)
  const hasMainCategories = (mainCategories?.length ?? 0) > 0

  return (
    <div className={classList}>
      {!isWorldCup && (
        <ErrorHandler location={location} component="CategoryNavigation">
          <CategoryNavigation
            title={i18n('markets.all_markets')}
            size="small"
            categories={mainCategories}
            categoriesLoading={categoriesStatus === constants.LOADING && !hasMainCategories}
            subcategories={isCategoryPage || showTagsBelowHeader || searchTerm ? [] : getSubcategories()}
            subcategoriesLoading={!showTagsBelowHeader && isOnVirtualCategory() && (
              isOnAllCategory() ? featuredCategoriesStatus === constants.LOADING : tagsStatus === constants.LOADING
            )}
            fullPath={getFullPath()}
            onTagSelect={isOnVirtualCategory() ? (
              isOnAllCategory()
                ? handleFeaturedTopic
                : (tagSlug) => setSelectedTag(tagSlug)
            ) : undefined}
            activeTag={isOnVirtualCategory() ? (
              isOnAllCategory() ? selectedTopicValue : selectedTag
            ) : undefined}
          />
        </ErrorHandler>
      )}
      {!isWorldCup && (
        <div className="md:hidden h-[24px]" />
      )}
      <div className={`view-feed__content-wrapper ${showSideMenu ? 'view-feed__content-wrapper--with-sidebar' : ''}`}>
        {showSideMenu && renderCategorySideMenu()}

        <div className="view-feed__content-main">
      {isWorldCup ? (
        <ErrorHandler location={location} component="WorldCupLobby">
          <Suspense
            fallback={
              <div className="px-4 md:px-[30px] py-8 animate-pulse space-y-4">
                <div className="h-8 w-64 rounded-lg bg-[var(--color-surface-3)]" />
                <div className="h-96 rounded-xl bg-[var(--color-surface-3)]" />
              </div>
            }>
            <WorldCupLobby />
          </Suspense>
        </ErrorHandler>
      ) : showSkeleton ? (
        <>
          {showHomeHighlight && <HomeHighlightSkeleton />}
          <FeedLoadingSkeleton cards={12} />
        </>
      ) : (
        <>
          {/* The midterm tag page opens on its map; the market grid follows it. */}
          {pathTagSlug === MIDTERMS_TAG_SLUG && (
            <ErrorHandler location={location} component="MidtermMap">
              <Suspense fallback={<MidtermMapSkeleton className="mb-6" />}>
                <MidtermMap />
              </Suspense>
            </ErrorHandler>
          )}

          {/* The strip opens on the event, which needs no data, so it mounts as
              soon as the home does; the market slides join it when they load. */}
          {showHomeHighlight && (
            <ErrorHandler location={location} component="HomeHighlight">
              <LazyComponentWrapper
                componentName="Home Highlight"
                fallback={<HomeHighlightSkeleton />}
                errorFallback={null}
                location={location}>
                <HomeHighlight markets={highlightMarkets} />
              </LazyComponentWrapper>
            </ErrorHandler>
          )}

          <ErrorHandler location={location} component="feed">
            <MarketListTemplate
              /* Sports markets carry the match data the fixture row lays out; every
                 other feed reads as title plus odds. */
              listViewKind={
                isSportsCategoryPath(pathnameForFeed) ? LIST_VIEW_KIND.FIXTURES : LIST_VIEW_KIND.MARKETS
              }
              activeMarketType={activeMarketType}
              queryParams={queryParams}
              markets={feedMarkets}
              status={feedStatus}
              pagination={feedPagination}
              /* The strip promoted these out of the grid, so they are counted by
                 `feedPagination.total` but never rendered as rows. Without this the grid's
                 loaded count can never reach its total and it keeps asking for empty pages. */
              hiddenRowsCount={hiddenRowsCount}
              /* Until the strip's own request lands the grid still holds the markets it
                 will promote, so the list's "first row" is not yet the first row. */
              marketsSettled={
                !showHomeHighlight || (heroStatus !== constants.IDLE && heroStatus !== constants.LOADING)
              }
              /* Everything that picks the rows besides the raw query string — the same
                 values that reset the feed above — so the list's selection starts over
                 with them. */
              listKey={`${activeMarketType}|${categoryId}|${selectedTag}|${selectedFeaturedCategory}|${priceFacetKey}`}
              error={error}
              gridInsert={feedInsert}
              returnUrl={feedUrl()}
              onLoadMore={(activeMarketType, options) => {
                // Always fetch for desktop column count (4 columns) regardless of screen size
                // This ensures consistent load more behavior across all devices
                const desktopColumnCount = BREAKPOINTS.default || 4
                const limit = desktopColumnCount * ITEMS_PER_COLUMN

                const loadMoreOptions = { ...options, ...queryParams, limit }
                delete loadMoreOptions.topic
                if (!isValidFeedOrdering(loadMoreOptions.ordering)) delete loadMoreOptions.ordering
                delete loadMoreOptions.window
                delete loadMoreOptions.asset

                /* The grid counts only the cards it renders, so the markets the
                   highlight strip promoted out of it would shift the offset back
                   onto pages already loaded. The feed's own length is the offset. */
                if (loadMoreOptions.startIndex) loadMoreOptions.startIndex = feedIds.length

                // Same section default as the first page, or the pages would reorder midway.
                const cleanPath = pathnameForFeed.replace(/\/$/, '')
                const isOnRoot = cleanPath === '' || cleanPath === '/markets'
                if (
                  !queryParams.ordering &&
                  !selectedTag &&
                  (isOnRoot ? !categoryId : isSportsCategoryPath(pathnameForFeed))
                ) {
                  loadMoreOptions.ordering = defaultFeedOrdering(pathnameForFeed)
                }

                if (categoryId) loadMoreOptions.categories = categoryId
                if (selectedFeaturedCategory) loadMoreOptions.categories = selectedFeaturedCategory
                if (priceFacetIds != null) loadMoreOptions.categories = priceFacetIds.join(',')
                if (selectedTag) loadMoreOptions.tag = selectedTag
                dispatch(fetchFeed(activeMarketType, loadMoreOptions))
              }}
              setMarketSelected={setMarketSelected}
              marketSelected={marketSelected}
              // `feed` keeps home's filter set; every path loads the next page on scroll.
              page={pathnameForFeed === feedUrl() ? 'feed' : ''}
              autoLoadMore
              breakpoints={activeBreakpoints}
              onBreakpointChange={handleBreakpointChange}
              onOpenSideMenu={isMobileSize ? () => setMobileSheetOpen(true) : () => setSideMenuOpen(true)}
              showSideMenuToggle={hasSideMenuContent && !isMobileSize && !sideMenuOpen}
              showMobileSideMenuToggle={hasSideMenuContent && isMobileSize}
              sideMenuOpen={sideMenuOpen}
              /* A `?tag=` chip refines the feed it sits under, so the heading
                 stays the tab's own name — Trending, New, All. `/tags/<slug>`
                 is the tag's own page and keeps naming itself. */
              categoryTitle={searchTerm || pathTagName || currentSubcategory?.title || rootCategory?.title || mainCategories.find(c => c.slug === categorySettings?.category)?.title || (() => {
                // The H1 mirrors the active virtual tab's name (home `/` is the Trending tab)
                const searchParams = new URLSearchParams(location.search)
                const ordering = searchParams.get('ordering')
                // Dropdown-only orderings (no tab of their own) share the "All" heading
                if (ordering === 'bet_end_date' || ordering === '-wagers_count') {
                  return i18n('markets.category_all')
                }
                if (isOnVirtualCategory()) {
                  if (ordering === '-volume') return i18n('markets.category_all')
                  if (ordering === '-created_on') return i18n('markets.category_new')
                  return i18n('markets.category_trending')
                }
                if (categorySettings?.category) {
                  return categorySettings.category
                    .split('-')
                    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
                    .join(' ')
                }
                return ''
              })()}
              titleAction={
                searchTerm || pathTagSlug ? (
                  /* Bare glyph, same affordance as the input's own clear icon */
                  <button
                    type="button"
                    onClick={handleClearHeading}
                    aria-label={i18n('search.clear_search')}
                    title={i18n('search.clear_search')}
                    className="inline-flex items-center justify-center p-1 rounded-[var(--radius-xs)] bg-transparent border-none text-Neutral-5 hover:text-Neutral-6 hover:bg-Neutral-2 cursor-pointer">
                    <CrossIcon width="12px" height="12px" />
                  </button>
                ) : null
              }
              emptyState={
                showClosedEventsEmptyState ? (
                  <EmptyState
                    className="!py-0"
                    title={i18n('markets.no_open_events')}
                    description=""
                    action={
                      <Button size="large" variant="primary" onClick={handleViewClosedEvents}>
                        {i18n('markets.view_closed_events')}
                      </Button>
                    }
                  />
                ) : null
              }
              headerClassName="mt-[32px] md:mt-0"
              subheader={searchTerm || showTagsBelowHeader ? tagsRow : null}
            />

            {/* {!currentSubcategory && pathnameForFeed === feedUrl() && <FAQs />} */}
          </ErrorHandler>
        </>
      )}
        </div>
      </div>

      {/* Mobile bottom sheet for category sidebar */}
      {hasSideMenuContent && isMobileSize && (
        <Modal
          isOpen={mobileSheetOpen}
          position="bottom"
          onCloseRequested={() => setMobileSheetOpen(false)}
          disableScrollLocking={false}
          title={rootCategory?.title || currentSubcategory?.title || ''}
          showCloseButton
          headerClassList="!flex !items-center !justify-between"
          closeButtonClassName="!static !p-2"
          containerClass="!px-0 !pt-0"
          scrollableContent
          actionSheetClassName="action-sheet--sticky-header"
          content={renderCategorySideMenu({ mobile: true })}
        />
      )}

      {renderHelmet()}
    </div>
  )
}

export default ViewFeed
